import { Button } from 'components/atoms';
import { useNotificationsConnection } from 'connections/notifications';
import { useHasPermission } from 'hooks';
import { useFirstAccessibleChildPath } from 'hooks/useFirstAccessibleChildPath';
import { useStoreState } from 'providers/StoreStateContext';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCaretDownBold, PiGearBold, PiPowerBold } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetUnreadThreadsCount } from 'services/chat';
import { useGetSettlementRequestsCount } from 'services/settlement';
import { useGetUnreadNotification } from 'services/notifications/index';
import { useGetLicenceConfiguration } from 'services/settings/individual';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { INavbarItem } from 'types/navbar/navbar.interface';
import { getParentPathname } from 'utils/navbar/getParentPathname';
import { navbarItems } from 'utils/navbar/navbarItems';
import './Navbar.scss';

const Navbar = () => {
  const [showChildren, setShowChildren] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { clearTokens, tokenData, applicationSettings } = useStoreState();

  const hasPermission = useHasPermission();

  const hasNotificationPermission = useMemo(
    () => hasPermission('NotificationPermissionsLIST**', 32),
    [hasPermission],
  );

  const productsFirstPath = useFirstAccessibleChildPath(
    'music-product-digital',
  );
  const contractsFirstPath = useFirstAccessibleChildPath('contracts');
  const settlementsFirstPath = useFirstAccessibleChildPath('statements');
  const ordersFirstPath = useFirstAccessibleChildPath('settlement-order');
  const clientFirstPath = useFirstAccessibleChildPath('client-users');

  const firstAccessiblePaths = useMemo(
    () => ({
      'music-product-digital': productsFirstPath,
      contracts: contractsFirstPath,
      statements: settlementsFirstPath,
      'settlement-order': ordersFirstPath,
      'client-users': clientFirstPath,
    }),
    [
      productsFirstPath,
      contractsFirstPath,
      settlementsFirstPath,
      ordersFirstPath,
      clientFirstPath,
    ],
  );

  useNotificationsConnection(hasNotificationPermission);

  const { data } = useGetUnreadNotification(hasNotificationPermission);
  const getLicenceConfiguration = useGetLicenceConfiguration();

  const customStyle = { justifyContent: 'start' };

  const isPageActive = (name: string): boolean =>
    getParentPathname(pathname) === name;

  const toggleChildren = (key: string) => {
    setShowChildren(prev => ({ [key]: !prev[key] }));
  };

  const navigateToPath = (item: INavbarItem, key: string) => {
    if (item.children) {
      toggleChildren(key);
      if (showChildren[key]) {
        return;
      }

      const firstAccessiblePath =
        firstAccessiblePaths[item.path as keyof typeof firstAccessiblePaths];
      if (firstAccessiblePath) {
        navigate(`/${firstAccessiblePath}`);
        return;
      }
    }

    navigate(`/${item.path}`);
  };

  const threadsUnreadCount = useGetUnreadThreadsCount(
    !!getLicenceConfiguration.data?.clientModuleAccess,
  );
  const settlementRequestsCount = useGetSettlementRequestsCount(
    !!getLicenceConfiguration.data?.clientModuleAccess,
  );

  return (
    <div className='navbar-wrapper'>
      {!!getLicenceConfiguration.data &&
        !!getLicenceConfiguration.data.isDemo && (
          <div className='navbar-demo-wrapper'>
            <p className='text'>Demo</p>
          </div>
        )}
      <div className='buttons'>
        {navbarItems(
          getLicenceConfiguration.data?.clientModuleAccess,
          applicationSettings?.extensionContract,
        ).map((item, key) =>
          item.permissionNames &&
          tokenData &&
          !item.permissionNames?.find(
            name => tokenData[name as keyof typeof tokenData],
          ) ? null : (
            <div
              key={key}
              className={`button-wrapper ${
                showChildren[key] ? 'show-sub-menu' : ''
              }`}>
              <Button
                isClear
                buttonStyle={customStyle}
                fullWidth
                isActive={
                  item.children?.length
                    ? !!item.children.find(({ path }) => isPageActive(path))
                    : isPageActive(item.path)
                }
                onClick={() => navigateToPath(item, key.toString())}
                className='button'>
                <Button.Icon icon={<item.icon />} />
                <Button.Text text={t(`navbar.${item.name ?? item.path}`)} />
                {!!data && item.path === 'notifications' && (
                  <Button.Label> {data > 99 ? '99+' : data}</Button.Label>
                )}
                {!!threadsUnreadCount.data &&
                  item.path === 'client-threads' &&
                  !showChildren[key] && (
                    <Button.Label>
                      {' '}
                      {threadsUnreadCount.data > 99
                        ? '99+'
                        : threadsUnreadCount.data}
                    </Button.Label>
                  )}
                {!!settlementRequestsCount.data &&
                  item.path === 'settlement-order' &&
                  !showChildren[key] && (
                    <Button.Label>
                      {' '}
                      {settlementRequestsCount.data > 99
                        ? '99+'
                        : settlementRequestsCount.data}
                    </Button.Label>
                  )}
                {item.children && (
                  <Button.Icon
                    icon={<PiCaretDownBold className='menu-arrow' />}
                  />
                )}
              </Button>
              {item.children && (
                <div className={`sub-menu`}>
                  {item.children.map((child, childKey) =>
                    child.permissionNames &&
                    tokenData &&
                    !child.permissionNames?.find(
                      name => tokenData[name as keyof typeof tokenData],
                    ) ? null : (
                      <Button
                        key={childKey}
                        isClear
                        size='compact'
                        buttonStyle={customStyle}
                        fullWidth
                        variant={
                          isPageActive(child.path) && child.path === 'channel'
                            ? BUTTON_VARIANT.remove
                            : undefined
                        }
                        isActive={
                          child.path === 'channel'
                            ? false
                            : isPageActive(child.path)
                        }
                        onClick={() => navigate(`/${child.path}`)}
                        className='button'>
                        <Button.Icon
                          icon={<child.icon />}
                          // child.path === 'channel' ? (
                          //   <YoutubeIcon />
                          // ) : (
                          //   <child.icon />
                          // )
                        />

                        <Button.Text
                          text={t(`navbar.${child.name ?? child.path}`)}
                        />
                        {!!threadsUnreadCount.data &&
                          child.path === 'client-threads' && (
                            <Button.Label>
                              {threadsUnreadCount.data > 99
                                ? '99+'
                                : threadsUnreadCount.data}
                            </Button.Label>
                          )}
                        {!!settlementRequestsCount.data &&
                          child.path === 'settlement-order' && (
                            <Button.Label>
                              {settlementRequestsCount.data > 99
                                ? '99+'
                                : settlementRequestsCount.data}
                            </Button.Label>
                          )}
                      </Button>
                    ),
                  )}
                </div>
              )}
            </div>
          ),
        )}
      </div>
      <div className='footer'>
        {/* <img
          src={`${process.env.PUBLIC_URL}/icons/magellup_logo.png`}
          alt='magellan_logo'
        /> */}
        <div className='footer-buttons'>
          <Button
            variant={BUTTON_VARIANT.default}
            fullWidth
            isClear
            onClick={() => navigate('/settings')}>
            <Button.Icon icon={<PiGearBold />} />
            <Button.Text text={t('navbar.settings')} />
          </Button>
          <Button
            variant={BUTTON_VARIANT.default}
            fullWidth
            onClick={clearTokens}>
            <Button.Icon icon={<PiPowerBold />} />
            <Button.Text text={t('navbar.logout')} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
